/* eslint-disable class-methods-use-this */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import { TransitionGroup, Transition } from 'react-transition-group'
import GSAP from 'react-gsap-enhancer'
import PropTypes from 'prop-types'
import { bem } from '@argos/utils'

import config from '../Core/Config'
import { isMobile } from '../Core/Helpers/device'
import { getDateIso } from '../Core/Helpers/dateTime'

import { CancelIcon, CommentIcon } from '../Common'

import { CoreCreators, ControlsCreators, FrameCreators, MessagingCreators } from '../actions'

import MinimizedLauncherIcon from './components/MinimizedLauncherIcon'

import {
  chatBtnWidth,
  minimizeChatBtnIn,
  minimizeChatBtnOut,
  startChatBtnIn,
  startChatBtnOut,
  endChatBtnIn,
  endChatBtnOut,
} from '../Core/Animations'

const cn = bem({ prefix: 'lc-', block: 'launcher' })

export const btnWidths = {
  start: 89,
  end: 117,
  continue: 163,
}

export class StartBtn extends Component {
  render() {
    return (
      <div className={cn('text-wrapper')}>
        <span className={cn('chat-icon-container')}>
          <CommentIcon height={18} className={cn('chat-icon')} />
        </span>
        <span className={cn('chat-text')}>Chat</span>
      </div>
    )
  }
}

export class EndBtn extends Component {
  render() {
    return (
      <div className={cn('text-wrapper')}>
        <span className={cn('close-icon-container')}>
          <CancelIcon className={cn('close-icon')} />
        </span>
        <span className={cn('close-text')}>End Chat</span>
      </div>
    )
  }
}

export class ContinueBtn extends React.Component {
  render() {
    return (
      <div className={cn('text-wrapper')}>
        <MinimizedLauncherIcon
          agentName={this.props.agentName}
          agentCount={this.props.agentCount}
          unreadMessages={this.props.unreadMessages}
          height={20}
          width={20}
        />
        <span className={cn('continue-text')}>Continue Chat</span>
      </div>
    )
  }
}

class ButtonTypes extends React.Component {
  render() {
    const { windowIsMinimized, windowIsActive, timeout } = this.props

    return (
      <TransitionGroup component={React.Fragment}>
        {windowIsMinimized && (
          <Transition timeout={timeout} onEnter={minimizeChatBtnIn} onExit={minimizeChatBtnOut}>
            <ContinueBtn {...this.props} />
          </Transition>
        )}

        {!windowIsMinimized && windowIsActive && (
          <Transition timeout={timeout} onEnter={endChatBtnIn} onExit={endChatBtnOut}>
            <EndBtn {...this.props} />
          </Transition>
        )}

        {!windowIsMinimized && !windowIsActive && (
          <Transition timeout={timeout} onEnter={startChatBtnIn} onExit={startChatBtnOut}>
            <StartBtn {...this.props} />
          </Transition>
        )}
      </TransitionGroup>
    )
  }
}

@GSAP()
class Launcher extends Component {
  static propTypes = {
    activeControl: PropTypes.oneOf(['Messenger', 'ExitPrompt', 'Form', 'WaitingRoom']),
    activeForm: PropTypes.string,
    agentName: PropTypes.string,
    agentCount: PropTypes.number,
    socketConnected: PropTypes.bool,
    unreadMessages: PropTypes.number,
    windowIsActive: PropTypes.bool,
    windowIsMinimized: PropTypes.bool,
    onSetChatStartTime: PropTypes.func.isRequired,
    onShowExitPrompt: PropTypes.func.isRequired,
    onResetApp: PropTypes.func.isRequired,
    onToggleActive: PropTypes.func.isRequired,
    onToggleMinimized: PropTypes.func.isRequired,
    onClearUnreadMessages: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)
    this.launcherRef = React.createRef()
  }

  componentWillReceiveProps(nextProps) {
    const { windowIsActive, windowIsMinimized } = this.props

    if (nextProps.windowIsMinimized !== windowIsMinimized || nextProps.windowIsActive !== windowIsActive) {
      this.handleButtonWidth()
    }
  }

  onToggleActive = () => {
    if (!this.props.windowIsActive) {
      this.props.onSetChatStartTime(getDateIso())
    }

    this.props.onToggleActive(!this.props.windowIsActive)
  }

  handleButtonWidth = () => {
    if (!isMobile()) {
      this.addAnimation(chatBtnWidth, { btnWidths })
    }
  }

  handleClick = () => {
    const label = this.getBtnType()

    if (label === 'end') {
      return this.handleEndChat()
    }

    if (label === 'continue') {
      this.props.onClearUnreadMessages()
      this.props.onToggleMinimized(false)
      return false
    }

    return this.onToggleActive()
  }

  handleEndChat = () => {
    if (this.props.socketConnected) {
      return this.showExitPrompt()
    }

    return this.props.onResetApp()
  }

  showExitPrompt() {
    if (this.props.activeControl !== 'ExitPrompt') {
      this.props.onShowExitPrompt()
    }
  }

  getBtnType = () => {
    if (this.props.windowIsMinimized) {
      return 'continue'
    }

    if (this.props.windowIsActive) {
      return 'end'
    }

    return 'start'
  }

  render() {
    const props = { role: 'button', type: 'button', onClick: this.handleClick }

    const timeout = !isMobile() ? config.launcherAnimSpeed * 1000 : 0

    return (
      <button ref={this.props.launcherRef} className={cn('', this.getBtnType())} {...props}>
        <ButtonTypes {...this.props} timeout={timeout} />
      </button>
    )
  }
}

const mapDispatchToProps = (dispatch) => ({
  onSetChatStartTime: (timestamp) => dispatch(FrameCreators.setChatStartTime(timestamp)),
  onShowExitPrompt: () => dispatch(ControlsCreators.setActiveControl('ExitPrompt')),
  onResetApp: () => dispatch(CoreCreators.resetApp()),
  onToggleActive: (toggled) => dispatch(FrameCreators.toggleActive(toggled)),
  onToggleMinimized: (toggled) => dispatch(FrameCreators.toggleMinimized(toggled)),
  onClearUnreadMessages: () => dispatch(MessagingCreators.clearUnreadMessages()),
})

const mapStateToProps = (state) => ({
  activeControl: state.Controls.activeControl,
  activeForm: state.Controls.activeForm,
  agentName: state.Chat.activeAgentName,
  agentCount: state.Chat.numberOfParticipants,
  socketConnected: state.Socket.socketConnected,
  unreadMessages: state.Messaging.unreadMessages,
  windowIsActive: state.Frame.windowIsActive,
  windowIsMinimized: state.Frame.windowIsMinimized,
})

export { Launcher }

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Launcher)
